import { useQuery } from "@tanstack/react-query";
import { getTrainingRun, listTrainingRuns } from "../../services/lorasApi";
import { loraQueryKeys, trainingRunQueryKeys } from "./queryKeys";
import type {
  TrainingRunStatusSlug,
  TrainingRunWithCheckpoints,
} from "../../types/loras";

const TERMINAL_STATUSES: ReadonlySet<TrainingRunStatusSlug> = new Set([
  "completed",
  "failed",
  "cancelled",
]);

/**
 * Single training run with progressively-registered checkpoints.
 *
 * WebSocket events (Phase 05: `lora.training.status_changed`,
 * `lora.checkpoint.added`) drive the realtime path. The 5s `refetchInterval`
 * is a polling fallback for the case where the websocket session is
 * disconnected or the event is missed; it auto-disables on terminal status.
 */
export function useTrainingRun(trainingRunId: string | undefined | null) {
  return useQuery<TrainingRunWithCheckpoints>({
    queryKey: trainingRunId
      ? trainingRunQueryKeys.detail(trainingRunId)
      : (["training-runs", "_disabled"] as const),
    queryFn: () => getTrainingRun(trainingRunId as string),
    enabled: Boolean(trainingRunId),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 5_000;
      return TERMINAL_STATUSES.has(data.statusSlug) ? false : 5_000;
    },
  });
}

export function useTrainingRuns(loraId: string | undefined | null) {
  return useQuery({
    queryKey: loraId
      ? loraQueryKeys.trainingRuns(loraId)
      : (["loras", "training-runs", "_disabled"] as const),
    queryFn: () => listTrainingRuns(loraId as string),
    enabled: Boolean(loraId),
  });
}
